import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/products/{code}/expand';
export type MakeGetProductExpandedByCodeDeps<ProductExpanded extends SchemaType.Top> = {
  readonly schemas: {
    readonly productExpanded: ProductExpanded;
  };
  readonly adapters: {
    readonly productExpanded: {
      readonly name: (product: ProductExpanded['Type']) => string | null;
    };
  };
  readonly rawUrlEncode: (input: string) => string;
};

export const makeGetProductExpandedByCode = <ProductExpanded extends SchemaType.Top>(
  deps: MakeGetProductExpandedByCodeDeps<ProductExpanded>
) => {
  const GetProductExpandedByCodeError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetProductExpandedByCode',
    path: ENDPOINT,
    identifierField: 'code',
  });

  const getProductExpandedByCodeFetch = (code: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { code: deps.rawUrlEncode(code) } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'product.code': code,
        },
      })
    );

  const getProductExpandedByCode = (code: string) =>
    getProductExpandedByCodeFetch(code).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.schemas.productExpanded)(response),
          404: (response) =>
            Effect.fail(new GetProductExpandedByCodeError.NotFoundError({ code, response })),
          orElse: (response) =>
            Effect.fail(new GetProductExpandedByCodeError.ApiRequestError({ code, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductGetExpanded,
          'Debug',
          `Successfully resolved product for code: ${code} -> name: ${deps.adapters.productExpanded.name(result) ?? 'default'}`
        )
      ),
      Effect.withSpan('api.product.getExpanded', {
        kind: 'internal',
        attributes: {
          'product.code': code,
        },
      })
    );

  return { getProductExpandedByCode, GetProductExpandedByCodeError };
};
